import { api, ApiError } from "@/lib/api";
import { parseFilenameFromHeader } from "@/lib/download";
import { consumeSse } from "@/lib/sse";
import type { ExtractionResult, ParsedExtraction } from "./types";

// POST /api/extract — multipart, one file at a time. Server returns the raw
// extraction (text + annotations + metadata + any table-region rows).
export async function extractFile(
  file: File,
  tableRegionsJson?: string,
  signal?: AbortSignal,
): Promise<ExtractionResult> {
  const form = new FormData();
  form.append("file", file);
  if (tableRegionsJson) form.append("tableRegionsJson", tableRegionsJson);

  return api<ExtractionResult>("/api/extract", {
    method: "POST",
    body: form,
    signal,
  });
}

// POST /api/parse — JSON; supports single (length 1) and batch (length 2+).
// Returns the parsed extraction for each input (same order).
export async function parseExtractions(
  extractionResults: ExtractionResult[],
  sessionId?: string,
  signal?: AbortSignal,
): Promise<ParsedExtraction[]> {
  return api<ParsedExtraction[]>("/api/parse", {
    method: "POST",
    body: JSON.stringify({ extractionResults, sessionId }),
    signal,
  });
}

// Streaming variant of /api/extract. Same multipart payload; response is
// text/event-stream. Progress messages flow into onProgress live; the final
// result resolves the promise.
export async function extractFileStreaming(
  file: File,
  opts: {
    tableRegionsJson?: string;
    onProgress: (msg: string) => void;
    signal?: AbortSignal;
  },
): Promise<ExtractionResult> {
  const form = new FormData();
  form.append("file", file);
  if (opts.tableRegionsJson) form.append("tableRegionsJson", opts.tableRegionsJson);

  const res = await fetch("/api/extract-stream", {
    method: "POST",
    credentials: "same-origin",
    body: form,
    signal: opts.signal,
  });

  return new Promise<ExtractionResult>((resolve, reject) => {
    consumeSse<ExtractionResult>(res, {
      onProgress: opts.onProgress,
      onResult: resolve,
      onError: (msg) => reject(new Error(msg)),
    });
  });
}

// Streaming variant of /api/parse.
export async function parseExtractionsStreaming(
  extractionResults: ExtractionResult[],
  opts: {
    sessionId?: string;
    onProgress: (msg: string) => void;
    signal?: AbortSignal;
  },
): Promise<ParsedExtraction[]> {
  const res = await fetch("/api/parse-stream", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      extractionResults,
      sessionId: opts.sessionId,
    }),
    signal: opts.signal,
  });

  return new Promise<ParsedExtraction[]>((resolve, reject) => {
    consumeSse<ParsedExtraction[]>(res, {
      onProgress: opts.onProgress,
      onResult: resolve,
      onError: (msg) => reject(new Error(msg)),
    });
  });
}

// POST /api/generate-pdf — JSON body is the verified ParsedExtraction.
// Returns the PDF blob; the server also sets a Content-Disposition header
// with the proper filename, which we extract here. sessionId links the
// archived document to its stats session; documentId targets the draft
// created at extraction time so generate finalizes it instead of inserting
// a duplicate row.
export async function generatePdf(
  data: ParsedExtraction,
  signal?: AbortSignal,
  sessionId?: string | null,
  documentId?: string | null,
): Promise<{ blob: Blob; filename: string }> {
  const params = new URLSearchParams();
  if (sessionId) params.set("sessionId", sessionId);
  if (documentId) params.set("documentId", documentId);
  const qs = params.size ? `?${params}` : "";
  const res = await fetch(`/api/generate-pdf${qs}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/pdf" },
    body: JSON.stringify(data),
    signal,
  });

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* swallow */
    }
    throw new ApiError(res.status, body);
  }

  const blob = await res.blob();
  const filename = parseFilenameFromHeader(res.headers.get("content-disposition"))
    ?? "ai1.pdf";
  return { blob, filename };
}

// POST /api/export-excel — JSON body is the verified ParsedExtraction.
// Returns an .xlsx blob + the server-supplied filename. sessionId lets the
// server finalize the stats session as Exported the moment the file is built;
// documentId targets the draft created at extraction time so the export
// finalizes it (an Excel export is a produced form — it counts on the
// dashboard and shows in History) instead of inserting a duplicate row.
export async function exportExcel(
  data: ParsedExtraction,
  signal?: AbortSignal,
  sessionId?: string | null,
  documentId?: string | null,
): Promise<{ blob: Blob; filename: string }> {
  const params = new URLSearchParams();
  if (sessionId) params.set("sessionId", sessionId);
  if (documentId) params.set("documentId", documentId);
  const qs = params.size ? `?${params}` : "";
  const res = await fetch(`/api/export-excel${qs}`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
    body: JSON.stringify(data),
    signal,
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* swallow */
    }
    throw new ApiError(res.status, body);
  }
  const blob = await res.blob();
  const filename =
    parseFilenameFromHeader(res.headers.get("content-disposition"))
    ?? `Objections_Export_${new Date()
      .toISOString()
      .replace(/[-:T.Z]/g, "")
      .slice(0, 14)}.xlsx`;
  return { blob, filename };
}


// =============================================================================
// Property info — saved CB / BIN / Applicant lookup by address
// =============================================================================

export interface PropertyInfoDto {
  id: number;
  normalizedAddress: string;
  originalAddress: string;
  cbNumber?: string | null;
  bin?: string | null;
  applicantName?: string | null;
  borough?: string | null;
  block?: string | null;
  lot?: string | null;
  createdAt: string;
  lastUsedAt: string;
  usageCount: number;
  sourceFileName?: string | null;
}

export async function getPropertyInfo(
  address: string,
  signal?: AbortSignal,
  // touch=false: read-only probe — no LastUsedAt/UsageCount bump. Use when
  // checking a candidate address you might not end up keeping.
  touch = true,
): Promise<PropertyInfoDto | null> {
  try {
    const q = `address=${encodeURIComponent(address)}${touch ? "" : "&touch=false"}`;
    return await api<PropertyInfoDto>(`/api/property-info?${q}`, { signal });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function savePropertyInfo(
  body: {
    address: string;
    cbNumber?: string | null;
    bin?: string | null;
    applicantName?: string | null;
    borough?: string | null;
    block?: string | null;
    lot?: string | null;
    sourceFileName?: string | null;
  },
  signal?: AbortSignal,
): Promise<void> {
  await api<void>("/api/property-info", {
    method: "POST",
    body: JSON.stringify(body),
    signal,
  });
}

// =============================================================================
// Stats session lifecycle
// =============================================================================

export type CorrectionType =
  | "Address"
  | "JobNumber"
  | "ObjectionText"
  | "DesignProfessional"
  | "JobDescription"
  | "InfractionType"
  | "Other";

export async function startSession(
  fileType: string,
  fileName: string,
): Promise<string> {
  const res = await api<{ sessionId: string }>("/api/sessions", {
    method: "POST",
    body: JSON.stringify({ fileType, fileName }),
  });
  return res.sessionId;
}

export async function recordObjection(
  sessionId: string,
  body: {
    address: string;
    jobNumber: string;
    objectionText?: string | null;
    wasEdited?: boolean;
  },
): Promise<string> {
  const res = await api<{ objectionId: string }>(
    `/api/sessions/${sessionId}/objection`,
    { method: "POST", body: JSON.stringify(body) },
  );
  return res.objectionId;
}

export async function recordCorrection(
  sessionId: string,
  body: {
    objectionId?: string | null;
    fieldName: string;
    originalValue: string;
    correctedValue: string;
    type: CorrectionType;
  },
): Promise<void> {
  await api<void>(`/api/sessions/${sessionId}/correction`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// Terminal outcome reported to /end. Mirrors the server SessionStatus enum;
// crosses the wire as the string name (JsonStringEnumConverter). "InProgress"
// is never sent by the client — a session left at InProgress means abandoned.
export type SessionStatus =
  | "Completed"
  | "Exported"
  | "Cancelled"
  | "Error";

export async function endSession(
  sessionId: string,
  body: {
    totalObjections: number;
    status: SessionStatus;
    resolvedObjections?: number;
    producedPdf?: boolean;
    producedExcel?: boolean;
    errorMessage?: string | null;
  },
): Promise<void> {
  await api<void>(`/api/sessions/${sessionId}/end`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
